import * as Sentry from '@sentry/react';

import { logError } from './errorLogger';

// Extend Window interface for Sentry initialization flag
declare global {
  interface Window {
    __SENTRY_INITIALIZED__?: boolean;
  }
}

/**
 * Initialize Sentry for error tracking and performance monitoring
 * Only initializes if DSN is provided (production environments)
 */
export const initSentry = (): void => {
  // Prevent multiple initializations (important for hot reloading)
  if (window.__SENTRY_INITIALIZED__) {
    return;
  }

  const dsn: string =
    (import.meta.env.VITE_SENTRY_DSN as string | undefined) ??
    'https://42b2f5b037b146954f4abfd9fb8b11c8@o4510536506081280.ingest.us.sentry.io/4510536507457536';

  if (!dsn) {
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release: (import.meta.env.VITE_RELEASE_VERSION ?? '1.0.0') as string,

    // Performance monitoring - BrowserTracing is included automatically in modern Sentry
    integrations: [
      // Replay integration (only if available)
      Sentry.replayIntegration({
        // Capture replays for 10% of sessions
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Performance monitoring sample rates
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1, // 10% in prod, 100% in dev
    replaysSessionSampleRate: import.meta.env.PROD ? 0.1 : 0.1, // 10% session replay
    replaysOnErrorSampleRate: 1, // 100% when errors occur

    // Error tracking configuration
    sendDefaultPii: true, // Send default PII data like IP addresses

    beforeSend: (event, hint) => {
      // Filter out known benign errors
      const error = hint.originalException as Error;
      const message = String(error.message).toLowerCase();
      const stack = String(error.stack ?? '').toLowerCase();

      // Filter out extension-related errors
      if (
        message.includes('ses_uncaught_exception') ||
        stack.includes('lockdown-install') ||
        message.includes('extension') ||
        message.includes('chrome-extension')
      ) {
        return null;
      }

      // Filter out network errors from external domains
      if (message.includes('network error') && message.includes('chrome-extension://')) {
        return null;
      }

      return event;
    },

    // Capture console errors in production
    beforeBreadcrumb: breadcrumb => {
      // Filter out noisy breadcrumbs in development
      if (import.meta.env.DEV && breadcrumb.category === 'console') {
        return null;
      }
      return breadcrumb;
    },
  });

  // Mark as initialized to prevent duplicate initializations
  window.__SENTRY_INITIALIZED__ = true;

  // Only log in production to reduce console noise in development
  // Sentry initialized with performance monitoring
};

/**
 * Set user context for better error tracking
 */
export const setUserContext = (
  user: { id: string; email?: string; username?: string; name?: string } | null
) => {
  if (user) {
    const sentryUser: { id: string; email?: string; username?: string } = {
      id: user.id,
    };
    if (user.email) sentryUser.email = user.email;
    if (user.username) sentryUser.username = user.username;
    else if (user.name) sentryUser.username = user.name;
    Sentry.setUser(sentryUser);
  } else {
    Sentry.setUser(null);
  }
};

/**
 * Set context for the current page/route
 */
export const setPageContext = (pageName: string, metadata: Record<string, unknown> = {}): void => {
  Sentry.setContext('page', {
    name: pageName,
    ...metadata,
  });
};

/**
 * Track custom events/metrics
 */
export const trackEvent = (eventName: string, data: Record<string, unknown> = {}): void => {
  Sentry.addBreadcrumb({
    category: 'custom',
    message: eventName,
    data,
    level: 'info',
  });
};

/**
 * Track performance metrics
 */
export const trackPerformance = (metricName: string, value: number, unit = 'ms'): void => {
  Sentry.addBreadcrumb({
    category: 'performance',
    message: `Performance: ${metricName}`,
    data: { value, unit },
    level: 'info',
  });
};

/**
 * Enhanced error reporting with Sentry
 * Wraps the existing error logger
 */
export const reportError = (error: unknown, context: Record<string, unknown> = {}) => {
  // Send to Sentry with enhanced context
  Sentry.withScope(scope => {
    scope.setLevel('error');

    // Add custom context
    if (context.source && typeof context.source === 'string') {
      scope.setTag('source', context.source);
    }

    if (context.severity && typeof context.severity === 'string') {
      scope.setLevel(context.severity as Sentry.SeverityLevel);
    }

    if (context.userId && typeof context.userId === 'string') {
      scope.setUser({ id: context.userId });
    }

    // Add extra context data
    scope.setContext('additional_info', {
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      ...context,
    });

    Sentry.captureException(error);
  });

  // Still use existing error logger as fallback
  logError(error, context);
};

/**
 * Core Web Vitals tracking for production
 */
export const trackWebVitals = (metric: {
  name: string;
  value: number;
  delta?: number;
  id?: string;
}) => {
  // Only track in production to avoid noise
  if (!import.meta.env.PROD) return;

  Sentry.addBreadcrumb({
    category: 'web-vitals',
    message: `Web Vital: ${metric.name}`,
    data: {
      value: metric.value,
      delta: metric.delta,
      id: metric.id,
      rating: getWebVitalRating(
        metric.name as 'CLS' | 'FID' | 'FCP' | 'LCP' | 'TTFB',
        metric.value
      ),
    },
    level:
      metric.value > getWebVitalThreshold(metric.name as 'CLS' | 'FID' | 'FCP' | 'LCP' | 'TTFB')
        ? 'warning'
        : 'info',
  });
};

/**
 * Get rating for web vital metrics
 */
const getWebVitalRating = (name: 'CLS' | 'FID' | 'FCP' | 'LCP' | 'TTFB', value: number) => {
  let threshold: { good: number; poor: number } | undefined;

  switch (name) {
    case 'CLS':
      threshold = { good: 0.1, poor: 0.25 };
      break;
    case 'FID':
      threshold = { good: 100, poor: 300 };
      break;
    case 'FCP':
      threshold = { good: 1800, poor: 3000 };
      break;
    case 'LCP':
      threshold = { good: 2500, poor: 4000 };
      break;
    case 'TTFB':
      threshold = { good: 800, poor: 1800 };
      break;
    default:
      return 'unknown';
  }

  if (value <= threshold.good) return 'good';
  if (value <= threshold.poor) return 'needs-improvement';
  return 'poor';
};

/**
 * Get threshold for web vital metrics
 */
const getWebVitalThreshold = (name: 'CLS' | 'FID' | 'FCP' | 'LCP' | 'TTFB'): number => {
  switch (name) {
    case 'CLS':
      return 0.25;
    case 'FID':
      return 300;
    case 'FCP':
      return 3000;
    case 'LCP':
      return 4000;
    case 'TTFB':
      return 1800;
    default:
      return 0; // This should never happen due to type constraints
  }
};
